<template>
  <v-alert
    v-if="filters.dates.to && filters.dates.from !== filters.dates.to"
    color="error"
    type="error"
    dark
    class="mt-2"
  >
    {{ $t("daily_report") }}
  </v-alert>
  <div v-else :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="8" lg="auto">
        <report-field-select-food-company></report-field-select-food-company>
      </v-col>
      <v-col cols="4" lg="auto">
        <shift-picker
          v-model="selectedShift"
          :items="shifts"
          :label="$t('shift.shift')"
          :disabled="!selectedCompany"
        />
      </v-col>
      <v-col cols="6" lg="auto">
        <dropdown
          :label="$t('view')"
          v-model="selectedLayout"
          :disabled="!selectedCompany"
          :items="layouts"
          hide-details
        ></dropdown>
      </v-col>
      <v-col cols="6" lg="auto">
        <autocomplete
          :label="$t('view')"
          multiple
          :items="headers"
          return-object
          v-model="visibleHeaders"
          hide-details
          select-all
        ></autocomplete>
      </v-col>
    </v-row>

    <v-data-table
      :headers="getMobileHeaders(sortedVisibleHeaders)"
      :items="customSearch(companyDailyOrders, sortedVisibleHeaders, filters.search)"
      class="striped-table ordera-box-shadow"
    >
      <template v-slot:item.num="{ index }">
        {{ index + 1 }}
      </template>
      <template v-slot:item.fullname="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ item.fullname }}
            </span>
          </template>
          <span>{{ item.userId }}</span>
        </v-tooltip>
      </template>
      <template v-for="category in mealCategories" v-slot:[`item.${category}`]="{ item }">
        <span v-if="Array.isArray(item[category]) && item[category].length">
          <template v-for="meal in item[category]">
            <!-- Layout 0: Količine i nazive jela -->
            <template v-if="selectedLayout === 0">
              <strong>{{ meal.quantity }}x</strong>
              <v-tooltip bottom v-if="meal.label !== $t('total')">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ toLatin(meal.name) }}
                  </span>
                </template>
                <span>{{ meal.id }}</span>
              </v-tooltip>
              <span v-else>{{ meal.label }}</span>
              <br v-if="item[category].indexOf(meal) < item[category].length - 1" />
            </template>
            <!-- Layout 1: Nazive jela -->
            <template v-else-if="selectedLayout === 1">
              <v-tooltip bottom v-if="meal.label !== $t('total')">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ toLatin(meal.name) }}
                  </span>
                </template>
                <span>{{ meal.id }}</span>
              </v-tooltip>
              <span v-else>{{ meal.label }}</span>
              <br v-if="item[category].indexOf(meal) < item[category].length - 1" />
            </template>
            <!-- Layout 2: Količine jela -->
            <template v-else-if="selectedLayout === 2">
              <strong>{{ meal.quantity }}</strong>
              <br v-if="item[category].indexOf(meal) < item[category].length - 1" />
            </template>
          </template>
        </span>
      </template>
      <template v-slot:body.append>
        <tr>
          <template v-for="header in sortedVisibleHeaders">
            <td style="font-weight: bold">
              <!-- Ako je kolona za redni broj -->
              <template v-if="header.value === 'num'">
                <!-- Prazno za redni broj -->
              </template>
              <!-- Ako je kolona za ime i prezime -->
              <template v-else-if="header.value === 'fullname'">
                <!-- Prazno za ime -->
              </template>
              <!-- Ako je kolona za datum -->
              <template v-else-if="header.value === 'date'">
                {{ $t("total") }}
              </template>
              <!-- Ako je kolona za napomenu/total -->
              <template v-else-if="header.value === 'total'">
                <!-- Prazno za napomenu -->
              </template>
              <!-- Ako je kolona za kategoriju jela -->
              <template v-else-if="mealCategories.includes(header.value)">
                {{
                  companyDailyOrders.reduce(
                    (sum, order) =>
                      sum +
                      (Array.isArray(order[header.value])
                        ? order[header.value].reduce(
                            (catSum, meal) => catSum + (meal.quantity || 0),
                            0
                          )
                        : 0),
                    0
                  )
                }}
              </template>
            </td>
          </template>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasFoodOrders from "@/mixins/report/hasFoodOrders";
import hasPrinting from "@/mixins/hasPrinting";
import ReportFieldSelectFoodCompany from "@/components/report/fields/ReportFieldSelectFoodCompany.vue";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasReloadWatch from "@/mixins/report/hasReloadWatch";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";

export default {
  name: "ReportTabGroupMealsTabOrders",
  mixins: [
    hasFoodOrders,
    hasPrinting,
    hasReportPrinting,
    hasExcel,
    hasReloadWatch,
    hasMobileHeaders,
  ],
  components: {
    ReportFieldSelectFoodCompany,
  },
  data() {
    return {
      users: [],
      orders: [],
      selectedLayout: 0,
      visibleHeaders: [],
    };
  },
  methods: {
    findUsers() {
      if (!this.selectedCompany) {
        this.users = [];
        return;
      }
      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };
      reportService
        .getCompanyFoodKorisniciByDay(this.selectedCompany.id, params)
        .then((response) => {
          this.setMealCategories(response.data);
          this.setShifts(response.data);
          this.orders = response.data;
        });
    },
  },
  computed: {
    layouts() {
      return [
        { text: this.$t("quantities_and_names"), value: 0 },
        { text: this.$t("names_only"), value: 1 },
        { text: this.$t("quantities_only"), value: 2 },
        { text: this.$t("spare_meals"), value: 3 },
      ];
    },
    userOrders() {
      return [...new Set(this.orders.map((item) => item.user_id))]
        .map((userId) => {
          const userOrders = this.orders.filter(
            (item) => item.user_id === userId && item.shift === this.selectedShift
          );
          if (!userOrders.length) return null;
          const dailyOrders = {
            userId,
            company_id: userOrders[0].company_id,
            date: userOrders[0].date,
            name: userOrders[0].name,
            lastname: userOrders[0].lastname,
            fullname: [userOrders[0].name, userOrders[0].lastname].join(" "),
            id: userOrders[0].id,
            note: userOrders.find((order) => order.note)?.note || "",
          };
          this.mealCategories.forEach((category) => {
            dailyOrders[category] = this.getDailyDish(userOrders, category);
          });
          return dailyOrders;
        })
        .filter(Boolean);
    },
    selectedCompany() {
      return this.$store.state.report.selectedCompany;
    },
    companyDailyOrders() {
      return this.userOrders
        .filter((order) => order.company_id === this.selectedCompany.id)
        .map((user) => {
          return {
            ...user,
            fullname: this.translate(user.fullname),
            note: this.translate(user.note),
          };
        });
    },
    printHeaders() {
      return this.sortedVisibleHeaders.filter((header) => {
        return !["action", "num", "id"].includes(header.value);
      });
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", ordering: 1 },

        { text: this.$t("name_surname"), value: "fullname", ordering: 2 },
        { text: this.$t("date"), value: "date", ordering: 3 },
        ...this.mealCategories.map((category, index) => ({
          text: this.translate(category),
          value: category,
          ordering: 4 + index,
        })),
        {
          text: this.$t("note"),
          value: "total",
          ordering: 4 + this.mealCategories.length,
        },
      ];
    },
    printItems() {
      return this.userOrders.map((user) => {
        const userData = {};

        // Dodaj samo podatke za visible headers
        this.sortedVisibleHeaders.forEach((header) => {
          if (header.value === "fullname") {
            userData.fullname = this.translate(user.fullname);
          } else if (header.value === "date") {
            userData.date = user.date;
          } else if (header.value === "total") {
            userData.total = this.translate(user.note);
          } else if (header.value === "num") {
            // num će biti dodан u watcheru
          } else if (this.mealCategories.includes(header.value)) {
            userData[header.value] = user[header.value]
              ? user[header.value].map((meal) => this.translate(meal.label)).join(", ")
              : "";
          }
        });

        return userData;
      });
    },
    filters() {
      return this.$store.state.report.filters;
    },
    sortedVisibleHeaders() {
      return [...this.visibleHeaders].sort(
        (a, b) => (a.ordering || 0) - (b.ordering || 0)
      );
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.findUsers();
      },
      deep: true,
    },
    headers: {
      handler() {
        this.visibleHeaders = [...this.headers].sort(
          (a, b) => (a.ordering || 0) - (b.ordering || 0)
        );
      },
      immediate: true,
    },
    selectedCompany: {
      handler() {
        this.findUsers();
      },
      immediate: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        const company = this.selectedCompany?.company || this.selectedCompany?.name;
        this.printDocument({
          tables: [
            {
              headers: this.printHeaders,
              items: this.printItems,
              showCaption: true,
            },
          ],
          title: [this.$t("report"), this.$t("orderers")].join(": "),
          subtitle: this.subtitle,
          beforeTableHtml: `<h4 style="text-align: center">${this.toLatin(company)}</h4>`,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("orderers")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("orderers"),
              headers: this.sortedVisibleHeaders,
              items: this.printItems.map((item, i) => ({
                ...item,
                num: i + 1,
              })),
              title: [this.$t("report"), this.$t("orderers")].join(" "),
              subtitle: this.subtitle,
              beforeTableHtml: `<h4 style="text-align: center">${this.selectedCompany?.company}</h4>`,
            },
          ],
        });
      }
    },
  },
};
</script>
